import { api, http } from '../client';
import type { UUID } from '@/types/domain';
import type { ImportHistoryParams, ImportPreview, ImportType } from '@/types/importing';

/*
 * Spreadsheet import: upload → validate → preview → confirm.
 *
 * Nothing here checks a row. Validation, duplicate detection and the
 * all-or-nothing commit happen on the server; the screen only shows what it
 * reported. IMPORT_MANAGE opens the tool, and the list of types is already
 * filtered by the caller's own create permissions.
 */
export const importsApi = {
  /** What the caller may import, with each template's columns. */
  types: () => api.get<ImportType[]>('/imports/types'),

  /** Saves the CSV template (header row plus one example row). */
  templateCsv: (type: string) =>
    downloadCsv(`/imports/${encodeURIComponent(type)}/template.csv`, `${type}-template.csv`),

  /** Validates a CSV file. Writes nothing but the preview. */
  validate: (type: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    // Content-Type left undefined so the browser sets multipart/form-data with its boundary.
    return api.post<ImportPreview>(`/imports/${encodeURIComponent(type)}/validate`, form, {
      headers: { 'Content-Type': undefined },
    });
  },

  /**
   * Confirms and imports, all or nothing, after re-validating against current
   * data. A file with errors imports only when `skipInvalid` is true.
   */
  commit: (id: UUID, skipInvalid: boolean) =>
    api.post<ImportPreview>(`/imports/${id}/commit`, undefined, { params: { skipInvalid } }),

  /** One import's preview or result. */
  get: (id: UUID) => api.get<ImportPreview>(`/imports/${id}`),

  /** Saves every row error as a CSV (the preview carries at most 500). */
  errorsCsv: (id: UUID) => downloadCsv(`/imports/${id}/errors.csv`, 'import-errors.csv'),

  /** Past imports, newest first. */
  history: (params: ImportHistoryParams) => api.getPage<ImportPreview>('/imports', { params }),
};

/**
 * Fetch a CSV and hand the browser a save prompt.
 *
 * Fetched as a blob rather than opened as a link: the request needs an
 * Authorization header, and a plain anchor cannot carry one. The filename
 * matches the one the server's Content-Disposition names.
 */
async function downloadCsv(path: string, filename: string): Promise<void> {
  const response = await http.get(path, { responseType: 'blob' });

  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
